import React, { useEffect, useRef, useState } from "react";
import { MessageBox } from "./ChatInput";
import { toast } from "react-toastify";
import { formatFileSize } from "../../Utils/formatSize";
import ChatVideoPlayer from "./ChatVideoPlayer";
import ChatAudioPlayer from "./ChatAudioPlayer";
import ChatEmbedMedia from "./ChatEmbedMedia.jsx";
import { Link } from "react-router-dom";
import linkifyHtml from "linkify-html";
import DOMPurify from "dompurify";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../Utils/firebase";
import JournalDetailModal from "../Modals/JournalDetailModal"; // Already in the original file
import parse from "html-react-parser";
import {
  extractHostedVideoEmbeds,
  parseLinkifiedHtmlWithVideoEmbeds,
} from "../../Utils/parseLinkifiedHtmlWithVideoEmbeds";
import HostedVideoEmbed from "../Common/HostedVideoEmbed.jsx";
import ChatLinkCard from "../Common/ChatLinkCard.jsx";

const SendChat = ({
  title,
  username,
  time,
  userImage,
  isFile,
  fileInfo,
  fileType,
  onDownload,
  messageId,
  isLiked,
  conversationId,
  replyCount,
  senderId,
  journal = {},
  showReply = true,
  onLike = () => {},
  onReply = () => {},
  userReaction,
  reactionSummary,
  replyMessage,
}) => {
  const [showActions, setShowActions] = useState(false);
  const [userData, setUserData] = useState(null);
  const reactions = [
    "👍",
    "❤️",
    "😂",
    "😮",
    "😢",
    "👎",
    "🔥",
    "✅",
    "🚀",
    "🙏",
    "💯",
  ];
  const [selectedReaction, setSelectedReaction] = useState(null);
  const [showReactions, setShowReactions] = useState(false);
  const reactionsRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        reactionsRef.current &&
        !reactionsRef.current.contains(event.target)
      ) {
        setShowReactions(false);
      }
    }

    if (showReactions) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showReactions]);

  const [showJournalModal, setShowJournalModal] = useState(false);

  // Check if message is a shared journal (Extracted from second file)
  const isJournalShared =
    journal?.isJournalShared &&
    journal?.journal_keywords &&
    journal?.user_journal_id &&
    journal?.emotion_name;

  // Format keywords for display (Extracted from second file)
  const formatKeywords = (keywords) => {
    if (!keywords || !Array.isArray(keywords)) return "";
    return keywords
      .map((keyword) => keyword.charAt(0).toUpperCase() + keyword.slice(1))
      .join(", ");
  };

  // Handle journal click (Extracted from second file)
  const handleJournalClick = () => {
    setShowJournalModal(true);
  };

  const handleLike = (e) => {
    e.stopPropagation();
    setShowReactions((prev) => !prev);
  };

  const handleReactionSelect = (emoji) => {
    setSelectedReaction(emoji);
    setShowReactions(false); // hide after selecting
    onLike(messageId, emoji);
  };

  useEffect(() => {}, [isLiked]);

  const handleReply = (e) => {
    e.stopPropagation();
    onReply({
      id: messageId,
      content: title ?? "",
      conversationId: conversationId,
    });
  };

  useEffect(() => {
    if (!senderId) return;

    const fetchUserData = async () => {
      try {
        const userRef = doc(db, "users", String(senderId));
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          setUserData(userSnap.data());
        } else {
          setUserData(null);
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };

    fetchUserData();
  }, [senderId]);

  // const parseMentions = (text) => {
  //   if (!text) return "";
  //   return text.replace(/(@\w+)/g, '<span class="send-mention">$1</span>');
  // };

  const html = linkifyHtml(DOMPurify.sanitize(title ?? ""), {
    target: "_blank",
    rel: "noopener noreferrer",
    attributes: { style: "color: white;" },
  });

  const replyHtml = linkifyHtml(
    DOMPurify.sanitize(replyMessage?.message ?? ""),
    {
      target: "_blank",
      rel: "noopener noreferrer",
      attributes: { style: "color: white;" },
    },
  );

  const hasMeaningfulHtml = (value = "") => {
    const plainText = value
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .trim();
    return plainText.length > 0;
  };

  const hasCaptionText = hasMeaningfulHtml(title);

  // Pull any pasted hosted-video links (YouTube / Vimeo / Loom / IG / FB) out of
  // the message HTML so they render as standalone players (matching the
  // uploaded-video UI) instead of sitting inside the blue chat bubble.
  const {
    videoUrls: hostedVideoUrls,
    linkUrls: sharedLinkUrls,
    textHtml: cleanedTextHtml,
    hasText: hasCleanedText,
  } = extractHostedVideoEmbeds(html, { keepLinkAnchorsInline: true });
  const hasHostedVideos = hostedVideoUrls.length > 0;
  const hasSharedLinks = sharedLinkUrls.length > 0;
  const hasStandaloneEmbeds = hasHostedVideos || hasSharedLinks;

  const renderChatEmbeds = () => (
    <ChatEmbedMedia videoUrls={hostedVideoUrls} linkUrls={sharedLinkUrls} />
  );

  return (
    <>
      <div
        id="journal-response2"
        className={` ${isLiked ? "liked" : ""}`}
        style={{ display: "flex", cursor: "pointer" }}
        // onClick={() => setShowActions((prev) => !prev)}
      >
        <div className="d-flex flex-column">
          <div
            className="mt-auto initial-journal-goal "
            id="journal-header"
            style={{ marginBottom: "0px" }}
          >
            <img
              className="user-profile-chat-image"
              src={userData?.profileImage || userData?.image || userImage}
              alt="Journal Icon"
              style={{ opacity: 1 }}
            />

            <div style={{ display: "flex", flexDirection: "column" }}>
              <div
                className="gap-1 gap-sm-2"
                style={{ display: "flex", alignItems: "center" }}
              >
                <span className="text-body-b2" style={{ color: "#e6ecf1" }}>
                  {/* Use fresh data from users collection if available, otherwise use prop */}
                  {userData?.name || userData?.full_name || username}
                </span>
                <span
                  className="text-caption-c2 align-self-end"
                  style={{ color: "#d0d8df", marginLeft: "12px" }}
                >
                  {time}
                </span>
              </div>
              <div
                className={`text-body-b2 ${
                  isFile && !isJournalShared ? "goals-text-message-part" : ""
                }`}
                style={{ position: "relative" }}
              >
                <div className="message-actions-wrapper">
                  {isFile ? (
                    <div className="file-message">
                      {fileType === "image" && fileInfo.url ? (
                        <>
                          <div
                            className="message-with-like-container"
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "8px",
                              flexWrap: "wrap",
                            }}
                          >
                            <div className="image-preview">
                              <img
                                src={fileInfo.url}
                                alt={fileInfo.name || "file preview"}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "200px",
                                  borderRadius: "8px",
                                  objectFit: "contain",
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  onDownload();
                                }}
                              />
                              {hasCaptionText && (
                                <div className="file-meta mt-2">
                                  {hasStandaloneEmbeds && renderChatEmbeds()}
                                  {hasCleanedText && (
                                    <div
                                      className="text-body-b2 goals-text-message-part message-content position-relative mb-0"
                                      style={{
                                        wordBreak: "break-word",
                                        flex: 1,
                                        alignItems: "center",
                                      }}
                                    >
                                      {parseLinkifiedHtmlWithVideoEmbeds(
                                        cleanedTextHtml,
                                        { keepLinkAnchorsInline: true },
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            {isLiked && (
                              <div
                                className="like-emoji-container mt-1"
                                style={{
                                  textAlign: "right",
                                  left: "14px",
                                  bottom: "-14px",
                                  position: "absolute",
                                }}
                              >
                                <span
                                  className="like-emoji"
                                  style={{
                                    fontSize: "18px",
                                    display: "inline-block",
                                    visibility: "visible",
                                    opacity: 1,
                                    zIndex: 0,
                                    position: "relative",
                                    color: "#FFD700",
                                    lineHeight: 1,
                                  }}
                                >
                                  👍
                                </span>
                              </div>
                            )}
                            {reactionSummary &&
                              Object.keys(reactionSummary).length > 0 && (
                                <div
                                  className="message-reactions"
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    marginTop: "4px",
                                  }}
                                >
                                  {Object.entries(reactionSummary)
                                    .filter(
                                      ([emoji, users]) =>
                                        emoji !== "like" && users.length > 0,
                                    )
                                    .map(([emoji, users]) => (
                                      <span
                                        key={emoji}
                                        style={{
                                          padding: "2px 6px",
                                          borderRadius: "12px",
                                          fontSize: "14px",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "4px",
                                        }}
                                      >
                                        {emoji} {users.length}
                                      </span>
                                    ))}
                                </div>
                              )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            className="message-with-like-container"
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "8px",
                              flexWrap: "wrap",
                              flexDirection: "column",
                            }}
                          >
                            <div
                              className="file-preview"
                              style={{
                                flexDirection: "column",
                                alignItems: "start",
                              }}
                            >
                              <div className="file-icon">
                                {fileType === "video" && (
                                  <span>
                                    <ChatVideoPlayer videoUrl={fileInfo.url} />
                                  </span>
                                )}
                                {fileType === "audio" && (
                                  <span>
                                    <ChatAudioPlayer src={fileInfo.url} />
                                  </span>
                                )}
                                {fileType === "document" && (
                                  <a
                                    href={fileInfo.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: "#b3ddff" }}
                                    className="text-decoration-none"
                                  >
                                    <span>
                                      {/* <span style={{ fontSize: "20px" }}>📄</span> */}
                                      📄
                                    </span>
                                  </a>
                                )}
                              </div>
                              <div className="file-meta">
                                <a
                                  href={fileInfo.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    onDownload();
                                  }}
                                  style={{ color: "#b3ddff" }}
                                  className="text-decoration-none"
                                >
                                  {/* <span style={{ fontSize: "20px" }}>📄</span> */}
                                </a>
                              </div>
                            </div>
                            {hasCaptionText && (
                              <>
                                {hasStandaloneEmbeds && (
                                  <div className="mt-2">
                                    {renderChatEmbeds()}
                                  </div>
                                )}
                                {hasCleanedText && (
                                  <div
                                    className="text-body-b2 goals-text-message-part message-content position-relative mb-0 mt-2"
                                    style={{
                                      wordBreak: "break-word",
                                      width: "fit-content",
                                      alignSelf: "flex-start",
                                    }}
                                  >
                                    {parseLinkifiedHtmlWithVideoEmbeds(
                                      cleanedTextHtml,
                                      { keepLinkAnchorsInline: true },
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                            {replyCount > 0 && (
                              <Link
                                to={`/threads?conversationId=${
                                  conversationId + "_" + messageId
                                }&messageId=${messageId}`}
                                className="reply-link"
                                style={{
                                  backgroundColor: "#008cff",
                                }}
                                state={{
                                  marginRight: "20px",
                                  marginTop: "auto",
                                  marginBottom: "auto",
                                }}
                              >
                                {replyCount} Replies
                              </Link>
                            )}
                            {isLiked && (
                              <div
                                className="like-emoji-container mt-1"
                                style={{
                                  textAlign: "right",
                                  left: "14px",
                                  bottom: "-14px",
                                  position: "absolute",
                                }}
                              >
                                <span
                                  className="like-emoji"
                                  style={{
                                    fontSize: "18px",
                                    display: "inline-block",
                                    visibility: "visible",
                                    opacity: 1,
                                    zIndex: 0,
                                    position: "relative",
                                    color: "#FFD700",
                                    lineHeight: 1,
                                  }}
                                >
                                  👍
                                </span>
                              </div>
                            )}
                            {reactionSummary &&
                              Object.keys(reactionSummary).length > 0 && (
                                <div
                                  className="message-reactions"
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    marginTop: "4px",
                                  }}
                                >
                                  {Object.entries(reactionSummary)
                                    .filter(
                                      ([emoji, users]) =>
                                        emoji !== "like" && users.length > 0,
                                    )
                                    .map(([emoji, users]) => (
                                      <span
                                        key={emoji}
                                        style={{
                                          padding: "2px 6px",
                                          borderRadius: "12px",
                                          fontSize: "14px",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "4px",
                                        }}
                                      >
                                        {emoji} {users.length}
                                      </span>
                                    ))}
                                </div>
                              )}
                          </div>
                          {/* {replyCount > 0 && replyHtml && (
                        <div className="reply-container">
                          <span className="fst-italic fw-light"> Reply :</span>
                          <div
                            dangerouslySetInnerHTML={{ __html: replyHtml }}
                            style={{ flex: 1, wordBreak: "break-word" }}
                          />
                        </div>
                      )} */}
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <div
                        className={`message-with-like-container ${
                          isJournalShared ||
                          (hasStandaloneEmbeds && !hasCleanedText)
                            ? ""
                            : "goals-text-message-part"
                        }`}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px",
                          flexWrap: "wrap",
                          flexDirection: "column",
                          position: "relative",
                        }}
                      >
                        {/* Standalone hosted-video players (outside the chat
                          bubble, matching the uploaded-video UI). */}
                        {hasStandaloneEmbeds && renderChatEmbeds()}

                        {hasCleanedText && (
                          <div style={{ flex: 1, wordBreak: "break-word" }}>
                            {parseLinkifiedHtmlWithVideoEmbeds(
                              cleanedTextHtml,
                              {
                                keepLinkAnchorsInline: true,
                              },
                            )}
                          </div>
                        )}
                        {/* Journal Sharing UI (INSERTED HERE) */}
                        {isJournalShared && (
                          <div style={{ width: "100%", marginTop: "8px" }}>
                            {/* Static text */}
                            <p
                              style={{
                                fontSize: "14px",
                                fontWeight: "400",
                                color: "#969FA7",
                                margin: "0 0 8px 0",
                              }}
                            >
                              User shared their {journal.emotion_name} reframe.
                              Click below to view detail.
                            </p>

                            {/* Journal preview box */}
                            <div
                              onClick={handleJournalClick}
                              style={{
                                backgroundColor: "#008CFF",
                                border: "2px solid white",
                                borderRadius: "14px",
                                padding: "12px 14px",
                                cursor: "pointer",
                                transition: "opacity 0.2s ease",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.opacity = "0.9")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.opacity = "1")
                              }
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  flexDirection: "row",
                                }}
                              >
                                {/* Keywords */}
                                <div
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    color: "#ffffff",
                                    marginBottom: "8px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "4px",
                                  }}
                                >
                                  {/* {formatKeywords(journal.journal_keywords)} */}
                                  {journal?.title_answer
                                    ? parse(journal.title_answer)
                                    : "NA"}
                                </div>

                                <img
                                  alt="arrow-icon"
                                  src="/icons/arrow-square-right.svg"
                                  style={{ width: "18px", height: "18px" }}
                                />
                              </div>
                            </div>

                            {/* Reply count link for journal messages */}
                            {replyCount > 0 && (
                              <Link
                                to={`/threads?conversationId=${conversationId + "_" + messageId}&messageId=${messageId}`}
                                className="reply-link"
                                style={{
                                  backgroundColor: "#008cff",
                                  marginTop: "8px",
                                  display: "inline-block",
                                }}
                              >
                                {replyCount} Replies
                              </Link>
                            )}
                          </div>
                        )}

                        {/* Reply count for non-journal messages */}
                        {!isJournalShared && replyCount > 0 && (
                          <Link
                            to={`/threads?conversationId=${
                              conversationId + "_" + messageId
                            }&messageId=${messageId}`}
                            className="reply-link"
                            style={{
                              backgroundColor: "#008cff",
                            }}
                            state={{
                              marginRight: "20px",
                              marginTop: "auto",
                              marginBottom: "auto",
                            }}
                          >
                            {replyCount} Replies
                          </Link>
                        )}
                        {isLiked && (
                          <div
                            className="like-emoji-container mt-1"
                            style={{
                              textAlign: "right",
                              right: "14px",
                              bottom: "-14px",
                              position: "absolute",
                            }}
                          >
                            <span
                              className="like-emoji"
                              style={{
                                fontSize: "18px",
                                display: "inline-block",
                                visibility: "visible",
                                opacity: 1,
                                zIndex: 0,
                                position: "relative",
                                color: "#FFD700",
                                lineHeight: 1,
                              }}
                            >
                              👍
                            </span>
                          </div>
                        )}

                        {reactionSummary &&
                          Object.keys(reactionSummary).length > 0 && (
                            <div
                              className="message-reactions"
                              style={{
                                display: "flex",
                                gap: "6px",
                                marginTop: "4px",
                              }}
                            >
                              {Object.entries(reactionSummary)
                                .filter(
                                  ([emoji, users]) =>
                                    emoji !== "like" && users.length > 0,
                                )
                                .map(([emoji, users]) => (
                                  <span
                                    key={emoji}
                                    style={{
                                      padding: "2px 6px",
                                      borderRadius: "12px",
                                      fontSize: "14px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    {emoji} {users.length}
                                  </span>
                                ))}
                            </div>
                          )}

                        {/* <p
                  className="text-body-b2"
                  style={{ marginBottom: "-12px" }}
                  dangerouslySetInnerHTML={{ __html: title }}
                ></p> */}
                      </div>
                    </>
                  )}
                  {/* Action buttons */}
                  {
                    <div className="message-actions-for-users actions-bar mt-2 gap-2">
                      <img
                        className="action-icon"
                        src="/icons/like-icon.svg"
                        onClick={handleLike}
                        alt="Like"
                        title="Like"
                      />
                      {showReactions && (
                        <div
                          ref={reactionsRef}
                          style={{
                            position: "absolute",
                            top: "-40px", // below the Like button
                            left: "-20px", // align left
                            display: "flex",
                            gap: "2px",
                            marginTop: "4px",
                            background: "#fff", // optional styling
                            padding: "4px 6px",
                            borderRadius: "6px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                            zIndex: 10,
                            overflowX: "auto", // vertical scroll if needed
                            maxWidth: "200px",
                          }}
                        >
                          {reactions.map((emoji, index) => (
                            <button
                              key={index}
                              onClick={() => handleReactionSelect(emoji)}
                              style={{
                                fontSize: "20px",
                                cursor: "pointer",
                                background: "transparent",
                                border: "none",
                              }}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                      {showReply && (
                        <img
                          className="action-icon ml-2"
                          src="/icons/reply-icon.svg"
                          onClick={handleReply}
                          alt="Reply"
                          title="Reply"
                        />
                      )}
                    </div>
                  }
                </div>
              </div>
              {/* Reply (Kept as is for now) */}
              {replyCount > 0 && (replyHtml || replyMessage?.file) && (
                <div className="reply-container mt-2">
                  <span className="fst-italic fw-light">Reply:</span>

                  {/* Text reply */}
                  {replyHtml && (
                    <div
                      style={{ flex: 1, wordBreak: "break-all", width: "100%" }}
                    >
                      {parseLinkifiedHtmlWithVideoEmbeds(replyHtml)}
                    </div>
                  )}
                  {/* File reply */}
                  {replyMessage?.file?.type === "video" &&
                    replyMessage?.file?.url && (
                      <div className="mt-2">
                        <ChatVideoPlayer videoUrl={replyMessage?.file.url} />
                      </div>
                    )}

                  {replyMessage?.file?.type === "audio" &&
                    replyMessage?.file?.url && (
                      <div className="mt-2">
                        <ChatAudioPlayer src={replyMessage?.file.url} />
                      </div>
                    )}

                  {replyMessage?.file?.type === "document" &&
                    replyMessage?.file?.url && (
                      <div className="mt-2">
                        <a
                          href={replyMessage?.file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#b3ddff" }}
                          className="text-decoration-none"
                        >
                          <span role="img" aria-label="document">
                            📄
                          </span>
                        </a>
                      </div>
                    )}

                  {replyMessage?.file?.type === "image" &&
                    replyMessage?.file?.url && (
                      <div className="mt-2">
                        <div className="image-preview">
                          <img
                            src={replyMessage?.file?.url}
                            alt={replyMessage?.file?.name || "file preview"}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "200px",
                              borderRadius: "8px",
                              objectFit: "contain",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              onDownload();
                            }}
                          />
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Journal Detail Modal (INSERTED HERE) */}
      <JournalDetailModal
        isOpen={showJournalModal}
        onClose={() => setShowJournalModal(false)}
        journal={journal}
        originalSender={{
          id: senderId,
          name: username,
          image: userImage,
        }}
        messageId={messageId}
        conversationId={conversationId}
        originalMessage={{
          content: title,
          senderId: senderId,
          senderName: username,
          senderProfileImage: userImage,
          journal: journal,
          isFile: isFile,
          file: fileInfo,
        }}
      />
    </>
  );
};

export default SendChat;
